/**
 * Deciding what to load, and making sure it is loaded before anyone acts on it.
 *
 * The translation catalogue used to be seven languages of static JSON built into
 * a table at module scope, so requiring `@/i18n` parsed all of it — and `@/i18n`
 * is on the boot graph via `api/client.ts`, in front of a first paint the root
 * layout already gates on `initI18n()`. A user reads one language; six were
 * being paid for.
 *
 * The fallback language is exempt. i18next needs it whatever the user picked,
 * and `TranslationKey` is derived from its shape, so it stays statically
 * imported and is never asked for here.
 *
 * The failure this guards against is not the loading — it is the gap between
 * loading and telling i18next. Anything that resolves before its bundle has
 * arrived renders a language the app does not hold, which is the flash of
 * untranslated UI. So `ensure` resolves only once the bundle is in place, and
 * callers await it before `changeLanguage`.
 *
 * No imports on purpose: this is reachable from the root `node --test` suites,
 * which cannot load anything that pulls `react-native`.
 */

type Options = {
  /** Always present, never loaded here. */
  fallback: string;
  isLoaded: (language: string) => boolean;
  load: (language: string) => Promise<unknown>;
  add: (language: string, bundle: unknown) => void;
};

export type LanguageLoader = {
  /**
   * Resolves once `language` is usable. Safe to call repeatedly and
   * concurrently; the bundle is parsed at most once.
   */
  ensure: (language: string) => Promise<void>;
};

export function createLanguageLoader(options: Options): LanguageLoader {
  const inFlight = new Map<string, Promise<void>>();

  return {
    ensure: async (language) => {
      if (language === options.fallback) return;
      if (options.isLoaded(language)) return;

      const running = inFlight.get(language);
      if (running) {
        // Two callers can resolve the same language in the same frame — the
        // initial resolve and the foreground language re-check. Both wait on the
        // one load rather than starting a second.
        await running;
        return;
      }

      const pending = (async () => {
        try {
          const bundle = await options.load(language);
          if (bundle) options.add(language, bundle);
        } catch {
          // Degraded, not broken: i18next's own `fallbackLng` answers every key,
          // so a missing bundle is English rather than a blank screen. Nothing
          // is recorded, so a transient failure is retried rather than becoming
          // permanent for the life of the process.
        } finally {
          inFlight.delete(language);
        }
      })();

      inFlight.set(language, pending);
      await pending;
    },
  };
}
